import { useState } from 'react';
import { customerApi } from '../customer_api';

interface RescheduleDrawerProps {
  bookingId: string;
  bookingType: 'boarding' | 'daycare' | 'grooming' | 'service';
  onSuccess: () => void;
  onClose: () => void;
}

/**
 * Self-serve reschedule drawer.
 *
 * Renders a minimal date picker (boarding = two dates, daycare/grooming =
 * single date+time). POSTs to /api/customer/bookings/:id/reschedule. The
 * server enforces the cancellation-window floor.
 */
export function RescheduleDrawer({
  bookingId,
  bookingType,
  onSuccess,
  onClose,
}: RescheduleDrawerProps) {
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsEnd = bookingType === 'boarding';
  const submit = async () => {
    if (!newStart) {
      setError('Pick a new start date.');
      return;
    }
    if (needsEnd && !newEnd) {
      setError('Pick a new end date.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: { newStart: string; newEnd?: string } = { newStart };
      if (needsEnd) body.newEnd = newEnd;
      await customerApi(`/api/customer/bookings/${bookingId}/reschedule`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onSuccess();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes('inside_cancellation_window')) {
        setError(
          'Your booking is within our cancellation window. Please contact us to reschedule.',
        );
      } else if (msg.includes('new_start_must_be_future')) {
        setError('Please pick a date in the future.');
      } else {
        setError(msg || 'Could not reschedule. Try again or contact us.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="reschedule-drawer"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '480px',
          background: 'var(--surface, white)',
          borderRadius: '14px 14px 0 0',
          padding: '1.5rem',
        }}
      >
        <h2
          style={{
            margin: '0 0 0.75rem',
            fontSize: '1.1rem',
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          Reschedule
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', opacity: 0.7 }}>
          Pick a new {needsEnd ? 'date range' : 'date'}. If we can't fit it we'll get in touch.
        </p>

        <label
          style={{
            display: 'block',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            opacity: 0.7,
            marginBottom: '0.25rem',
          }}
        >
          {needsEnd ? 'New check-in' : 'New date'}
        </label>
        <input
          type={needsEnd ? 'date' : 'datetime-local'}
          value={newStart}
          onChange={(e) => setNewStart(e.target.value)}
          data-testid="reschedule-new-start"
          style={{
            width: '100%',
            padding: '0.65rem 0.75rem',
            borderRadius: '8px',
            border: '1px solid var(--border, oklch(0.915 0.005 80))',
            marginBottom: needsEnd ? '0.85rem' : '1.25rem',
            fontSize: '0.9rem',
          }}
        />
        {needsEnd && (
          <>
            <label
              style={{
                display: 'block',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                opacity: 0.7,
                marginBottom: '0.25rem',
              }}
            >
              New check-out
            </label>
            <input
              type="date"
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              data-testid="reschedule-new-end"
              style={{
                width: '100%',
                padding: '0.65rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
                marginBottom: '1.25rem',
                fontSize: '0.9rem',
              }}
            />
          </>
        )}
        {error && (
          <p
            role="alert"
            style={{
              color: 'var(--destructive, oklch(0.55 0.15 30))',
              fontSize: '0.85rem',
              margin: '0 0 0.75rem',
            }}
          >
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.7rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="reschedule-submit"
            style={{
              flex: 2,
              padding: '0.7rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Submitting…' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
